/**
 * Error re-exports from @morfi/errors.
 * The SDK defines ZERO error codes — all come from the generated errors package.
 */

import type {
  CpSwapErrorName,
  OpinionErrorDef,
  OpinionErrorName,
  SellCapErrorName,
} from '@morfi/errors';
import {
  CP_SWAP_ERROR_BY_CODE,
  OPINION_ERROR_BY_CODE,
  OPINION_ERRORS,
  SELL_CAP_ERROR_BY_CODE,
  SELL_CAP_ERRORS,
} from '@morfi/errors';
import { CP_SWAP_PROGRAM_ID, MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';

export type { CpSwapErrorName, OpinionErrorDef, OpinionErrorName, SellCapErrorName };
export { OPINION_ERRORS, SELL_CAP_ERRORS };

/**
 * Markets error by code. Sell-cap-only codes follow the classic program's and
 * are never raised by it, so one lookup serves both program variants.
 */
function marketsErrorByCode(code: number): OpinionErrorDef | undefined {
  return OPINION_ERROR_BY_CODE.get(code) ?? SELL_CAP_ERROR_BY_CODE.get(code);
}

/**
 * Which on-chain program an error code resolved against. cp-swap and markets
 * share part of the 6000+ Anchor error range, so this distinguishes them.
 */
export type ErrorProgram = 'markets' | 'cp-swap';

export interface ResolvedError {
  name: string;
  msg: string;
  program: ErrorProgram;
}

/**
 * Resolve a numeric error code to its definition, disambiguating the
 * markets/cp-swap overlap by the originating program id when known.
 *
 * - `programId === cp-swap` → cp-swap map only.
 * - `programId === markets` → markets map only.
 * - unknown/omitted → markets first (the dominant path), then cp-swap.
 */
export function resolveErrorByCode(code: number, programId?: string): ResolvedError | undefined {
  if (programId === CP_SWAP_PROGRAM_ID.toBase58()) {
    const def = CP_SWAP_ERROR_BY_CODE.get(code);
    return def ? { name: def.name, msg: def.msg, program: 'cp-swap' } : undefined;
  }
  if (programId === MORFI_OPINION_PROGRAM_ID.toBase58()) {
    const def = marketsErrorByCode(code);
    return def ? { name: def.name, msg: def.msg, program: 'markets' } : undefined;
  }
  if (programId !== undefined) {
    // A known program id that is neither markets nor cp-swap — e.g. the inner
    // CPI that actually failed (token program, an unrelated Anchor program).
    // Solana logs the innermost failure FIRST, so this is the originating
    // program; do NOT guess it against the markets/cp-swap maps (a third
    // program's 6000+ code would be mis-named). Let the caller's lower tiers
    // (SPL / Anchor framework) handle it.
    return undefined;
  }
  // No program context at all (bare InstructionError, no logs): markets is the
  // dominant path, so try it first, then cp-swap.
  const markets = marketsErrorByCode(code);
  if (markets) {
    return { name: markets.name, msg: markets.msg, program: 'markets' };
  }
  const cpSwap = CP_SWAP_ERROR_BY_CODE.get(code);
  if (cpSwap) {
    return { name: cpSwap.name, msg: cpSwap.msg, program: 'cp-swap' };
  }
  return undefined;
}

/**
 * Lookup an error definition by its numeric code (6000+). Markets-only;
 * prefer {@link resolveErrorByCode} when a program id is available.
 */
export function getErrorByCode(code: number): { name: string; msg: string } | undefined {
  const def = marketsErrorByCode(code);
  return def ? { name: def.name, msg: def.msg } : undefined;
}

export type { MorfiError } from './parser.js';
export { parseTransactionError } from './parser.js';
